import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, Loader2, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function HostLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      await login('host', { email, password, remember });
      toast.success('Welcome back');
      nav('/host/dashboard');
    } catch (e2) {
      setErr(e2?.message || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1600" className="absolute inset-0 h-full w-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/75 via-teal-800/55 to-slate-900/75" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-white/10 backdrop-blur border border-white/20 grid place-items-center font-bold text-[13px]">SE</div>
            <div>
              <div className="font-semibold leading-tight text-[15px] tracking-tight">StayEasy</div>
              <div className="text-[10.5px] opacity-70 leading-tight uppercase tracking-[0.09em] font-medium mt-0.5">Host Console</div>
            </div>
          </div>
          <div>
            <h2 className="text-[40px] font-semibold leading-[1.05] tracking-[-0.028em] text-balance">
              Grow your hosting business with <span className="font-serif italic font-normal">confidence.</span>
            </h2>
            <p className="mt-5 text-white/75 max-w-md text-[14.5px] leading-relaxed">Track bookings, reply to guests, manage payouts and keep your listings sharp — all from one place.</p>
          </div>
          <div className="text-[11px] opacity-60 font-mono">© 2026 StayEasy — console.stayeasy.io/host</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-teal-700 text-white grid place-items-center font-bold">SE</div>
            <div className="font-semibold text-slate-900 tracking-tight">StayEasy</div>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold border border-teal-100 mb-5">
            <Building2 className="h-3 w-3" strokeWidth={2.5} /> Host sign-in
          </div>
          <h1 className="text-[30px] font-semibold text-slate-900 tracking-[-0.028em] leading-tight">Welcome back</h1>
          <p className="text-slate-500 mt-1.5 text-[14px]">Sign in to manage your properties and bookings.</p>

          {err && (
            <div className="mt-5 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">
              <AlertTriangle className="h-4 w-4 mt-0.5" /> <span>{err}</span>
            </div>
          )}

          <form onSubmit={handle} className="space-y-4 mt-6">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="you@example.com" required autoComplete="email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <a href="#" className="text-xs text-teal-700 hover:underline">Forgot password?</a>
              </div>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-11" placeholder="••••••••" required autoComplete="current-password" />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute inset-y-0 right-0 grid w-11 place-items-center text-slate-400 transition-colors hover:text-slate-700">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={remember} onCheckedChange={setRemember} />
              <Label htmlFor="remember" className="text-sm text-slate-600 font-normal">Remember me for 30 days</Label>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-xs text-slate-400 text-center">
            Are you a platform admin? <Link to="/admin/login" className="underline">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
